import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Loader2, Plus, Pencil, Trash2, MapPin, ArrowRight, History } from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

export default function AutomezziLocazioniPage() {
  const { t } = useTranslation();
  const [locazioni, setLocazioni] = useState([]);
  const [automezzi, setAutomezzi] = useState([]);
  const [centriCosto, setCentriCosto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [form, setForm] = useState({
    automezzo_id: "",
    data_spostamento: new Date().toISOString().split('T')[0],
    centro_costo_origine_id: "",
    centro_costo_destinazione_id: "",
    note: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const [locRes, autoRes, ccRes] = await Promise.all([
        api.get(`${prefix}/automezzi/locazioni/lista`),
        api.get(`${prefix}/automezzi`),
        api.get(`${prefix}/cost-centers`)
      ]);
      setLocazioni(locRes.data);
      setAutomezzi(autoRes.data);
      setCentriCosto(ccRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAddDialog = () => {
    setEditingId(null);
    setForm({
      automezzo_id: "",
      data_spostamento: new Date().toISOString().split('T')[0],
      centro_costo_origine_id: "",
      centro_costo_destinazione_id: "",
      note: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setForm({
      automezzo_id: item.automezzo_id || "",
      data_spostamento: item.data_spostamento || "",
      centro_costo_origine_id: item.centro_costo_origine_id || "",
      centro_costo_destinazione_id: item.centro_costo_destinazione_id || "",
      note: item.note || ""
    });
    setDialogOpen(true);
  };

  // Quando si seleziona un veicolo, precompila la locazione di origine
  const handleVeicoloChange = (veicoloId) => {
    const veicolo = automezzi.find(a => a.id === veicoloId);
    setForm({ 
      ...form, 
      automezzo_id: veicoloId,
      centro_costo_origine_id: veicolo?.centro_costo_id || ""
    });
  };

  const handleSubmit = async () => {
    if (!form.automezzo_id || !form.data_spostamento || !form.centro_costo_destinazione_id) {
      handleApiError({ message: t("errors.requiredFields") });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        data_spostamento: form.data_spostamento,
        centro_costo_origine_id: form.centro_costo_origine_id && form.centro_costo_origine_id !== "__none__" ? form.centro_costo_origine_id : null,
        centro_costo_destinazione_id: form.centro_costo_destinazione_id,
        note: form.note || null
      };
      
      if (editingId) {
        await api.put(`${getApiPrefix()}/automezzi/locazioni/${editingId}`, payload);
        showSuccess(t("success.transferUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/automezzi/locazioni`, {
          ...payload,
          automezzo_id: form.automezzo_id
        });
        showSuccess(t("success.transferRegistered"));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`${getApiPrefix()}/automezzi/locazioni/${deleteDialog.item.id}`);
      showSuccess(t("success.transferDeleted"));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    for (const id of ids) {
      try {
        await api.delete(`${getApiPrefix()}/automezzi/locazioni/${id}`);
        deleted++;
      } catch (err) {}
    }
    showSuccess(`${deleted} spostamenti eliminati`);
    fetchData();
  };

  // Stats
  const totaleSpostamenti = locazioni.length;
  const veicoliSpostati = new Set(locazioni.map(l => l.automezzo_id)).size;

  const columns = [
    {
      key: 'veicolo',
      label: 'Veicolo',
      getValue: (row) => `${row.marca} ${row.modello}`,
      render: (value, row) => (
        <div>
          <span className="font-medium">{value}</span>
          <div className="text-xs text-muted-foreground font-mono">{row.targa}</div>
        </div>
      )
    },
    { key: 'data_spostamento', label: t('common.date') },
    {
      key: 'centro_costo_origine_nome',
      label: 'Da',
      render: (v) => v || <span className="text-muted-foreground italic">Non definita</span>
    },
    {
      key: 'spostamento',
      label: '',
      sortable: false,
      filterable: false,
      exportable: false,
      render: () => <ArrowRight className="h-4 w-4 text-muted-foreground" />
    },
    {
      key: 'centro_costo_destinazione_nome',
      label: 'A',
      render: (v) => <span className="font-medium">{v}</span>
    },
    { key: 'note', label: t('common.notes'), render: (v) => v || '-' },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDialog(row)}
            data-testid={`edit-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteDialog({ open: true, item: row })}
            className="text-destructive"
            data-testid={`delete-${row.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="locazioni-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("nav.transfers")}</h1>
        <p className="text-muted-foreground text-sm">Traccia gli spostamenti dei veicoli tra le sedi</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <History className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t("transfers.totalTransfers")}</p>
                <p className="text-2xl font-bold tracking-tight">{totaleSpostamenti}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <MapPin className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Veicoli con Spostamenti</p>
                <p className="text-2xl font-bold text-green-600">{veicoliSpostati}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('common.movementHistory')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={locazioni}
            onDelete={handleBulkDelete}
            idField="id"
            emptyMessage="Nessuno spostamento registrato"
            exportFileName="storico_locazioni"
            toolbar={
              <Button onClick={openAddDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Registra Spostamento
              </Button>
            }
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? t("transfers.editTransfer") : t("transfers.newTransfer")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('leasing.vehicle')} *</Label>
              <SearchableSelect
                options={automezzi.map(a => ({ value: a.id, label: `${a.marca} ${a.modello} - ${a.targa}${a.centro_costo_nome ? ` (${a.centro_costo_nome})` : ''}` }))}
                value={form.automezzo_id}
                onValueChange={handleVeicoloChange}
                disabled={!!editingId}
                placeholder={t("dialogs.selectVehicle")}
                searchPlaceholder="Cerca veicolo..."
                emptyMessage="Nessun veicolo trovato"
              />
            </div>
            <div className="space-y-2">
              <Label>Data Spostamento *</Label>
              <DateInput value={form.data_spostamento} onChange={(e) => setForm({ ...form, data_spostamento: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Da (Origine)</Label>
                <SearchableSelect
                  options={[{ value: "__none__", label: "Non definita" }, ...centriCosto.map(cc => ({ value: cc.id, label: cc.nome }))]}
                  value={form.centro_costo_origine_id || "__none__"}
                  onValueChange={(v) => setForm({ ...form, centro_costo_origine_id: v === "__none__" ? "" : v })}
                  placeholder={t("common.selectPlaceholder")}
                  searchPlaceholder={t("common.searchPlaceholder")}
                  emptyMessage="Nessun CDC trovato"
                />
              </div>
              <div className="space-y-2">
                <Label>A (Destinazione) *</Label>
                <SearchableSelect
                  options={centriCosto.map(cc => ({ value: cc.id, label: cc.nome }))}
                  value={form.centro_costo_destinazione_id}
                  onValueChange={(v) => setForm({ ...form, centro_costo_destinazione_id: v })}
                  placeholder={t("common.selectPlaceholder")}
                  searchPlaceholder={t("common.searchPlaceholder")}
                  emptyMessage="Nessun CDC trovato"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Es: Trasferimento per lavori" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : (editingId ? t("common.save") : t("common.save"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog({ open, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>Eliminare questo spostamento?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })} disabled={deleting}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
